import dayjs from 'dayjs';
import {ChangeEvent, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {
  useMyCoupons,
  usePrependMyCoupon,
  useRefetchMyCoupons,
  useRegisterCouponCode,
  useUseCoupon,
} from '@/api/coupon';
import {
  getGqlErrorDetailData,
  getGqlErrorMessage,
  isAccessRestrictionError,
} from '@/api/request';
import {useRefetchMe} from '@/api/session';
import {Button} from '@/components/Button';
import {Dialog} from '@/components/Dialog';
import toast from '@/components/HotToast';
import {Modal} from '@/components/Modal';
import {TextField} from '@/components/TextField';
import {Typography} from '@/components/Typography';
import {COUPON_CODE_LENGTH, COUPON_EVENTS, COUPON_MESSAGES} from '@/constants';
import {CouponApplyType, CouponRegisterFailReason} from '@/enums';
import {useCheckAccessRestriction, useSession} from '@/hooks';
import {useGA4WithParams} from '@/hooks/tracking';
import {MyCoupon} from '@/interface';
import {paths} from '@/router';
import {
  ColumnFlexBox,
  Flexbox,
  FlexFullGrow,
  interceptProps,
} from '@/styled-components';
import {getHexWithOpacity, palette, Z_INDEX} from '@/styles';
import {
  formatCouponCode,
  getCouponConfirmMessage,
  getCouponDeadlineDDay,
  getCouponSource,
  hasActiveUsePass,
  isValidCouponCodeFormat,
  normalizeCouponCode,
  getCouponCodeType,
} from '@/utils';
import {CouponListItem} from './CouponListItem';

interface IProps {
  onClose(): void;
}

interface InlineMessage {
  type: 'error' | 'success';
  text: string;
}

/** 쿠폰 이용 일수 (GA days 파라미터) */
const getCouponDays = (coupon: MyCoupon) => {
  if (coupon.applyType === CouponApplyType.Relative) {
    return coupon.durationDays ?? 0;
  }

  return dayjs(coupon.useEndedAt).diff(dayjs(coupon.useBeganAt), 'day') + 1;
};

export const MyCouponModal = ({onClose}: IProps) => {
  const navigate = useNavigate();
  const tracking = useGA4WithParams();
  const {user, isPaidUser} = useSession();
  const {data: coupons, isPending, isError, refetch} = useMyCoupons(user?.id);
  const prependMyCoupon = usePrependMyCoupon();
  const refetchMyCoupons = useRefetchMyCoupons();
  const refetchMe = useRefetchMe();
  const {mutate: registerCouponCode, isPending: isRegistering} =
    useRegisterCouponCode();
  const {mutate: startCoupon, isPending: isUsing} = useUseCoupon();
  const {checkAccessRestriction, isChecking} = useCheckAccessRestriction();

  const [code, setCode] = useState<string>('');
  const [message, setMessage] = useState<InlineMessage>();
  const [selectedId, setSelectedId] = useState<string>();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  const hasActivePass = hasActiveUsePass(coupons, isPaidUser);
  const activePass = hasActivePass ? 'Y' : 'N';
  const selectedCoupon = useMemo(
    () => coupons?.find((i) => i.id === selectedId),
    [coupons, selectedId],
  );

  const handleChangeCode = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(formatCouponCode(e.target.value));
    setMessage(undefined);
  };

  const handleRegister = async () => {
    if (await checkAccessRestriction()) {
      return;
    }

    const normalized = normalizeCouponCode(code);
    const formatValid = isValidCouponCodeFormat(normalized);

    tracking(COUPON_EVENTS.codeSubmit, {
      format_valid: formatValid ? 'Y' : 'N',
      active_pass: activePass,
    });

    if (!formatValid) {
      setMessage({type: 'error', text: COUPON_MESSAGES.formatError});
      tracking(COUPON_EVENTS.codeFail, {
        reason: CouponRegisterFailReason.format,
        active_pass: activePass,
      });
      return;
    }

    registerCouponCode(
      {code: normalized},
      {
        onSuccess: (data) => {
          const registered = data?.result;

          if (!registered) {
            setMessage({
              type: 'error',
              text: COUPON_MESSAGES.registerServerError,
            });
            tracking(COUPON_EVENTS.codeFail, {
              reason: CouponRegisterFailReason.server,
              active_pass: activePass,
            });
            return;
          }

          // 등록 응답이 목록 원소와 동일한 형태라 최상단에 바로 추가하고,
          // 같은 쿠폰이 여러 장이면 기존 카드에도 (1)(2)가 붙도록 다시 불러온다.
          prependMyCoupon(user?.id, registered);
          refetchMyCoupons();
          setCode('');
          setMessage({
            type: 'success',
            text: registered.usableNow
              ? COUPON_MESSAGES.registerSuccess
              : COUPON_MESSAGES.registerSuccessWhileActive,
          });
          tracking(COUPON_EVENTS.codeSuccess, {
            active_pass: activePass,
            voucher_product_id: registered.id,
            days: getCouponDays(registered),
            coupon_name: registered.name,
            code_type: getCouponCodeType(registered.issueRoute),
          });
        },
        onError: (error) => {
          // 이용 제한은 공통 에러 핸들러가 안내 모달을 띄운다.
          if (isAccessRestrictionError(error)) {
            return;
          }

          setMessage({
            type: 'error',
            text: getGqlErrorMessage(
              error,
              COUPON_MESSAGES.registerServerError,
            ),
          });

          const detail = getGqlErrorDetailData(error);
          const {reason, couponName, codeType} = detail?.extra ?? {};

          // coupon_name·code_type 은 코드가 쿠폰으로 식별될 때만 서버가 내려준다
          tracking(COUPON_EVENTS.codeFail, {
            reason:
              reason ??
              (detail?.code === 400
                ? CouponRegisterFailReason.invalid
                : CouponRegisterFailReason.server),
            active_pass: activePass,
            ...(couponName && {coupon_name: couponName, code_type: codeType}),
          });
        },
      },
    );
  };

  const handleSelect = (coupon: MyCoupon) => {
    const deselect = selectedId === coupon.id;

    setSelectedId(deselect ? undefined : coupon.id);
    tracking(COUPON_EVENTS.itemSelect, {
      action: deselect ? 'deselect' : 'select',
      voucher_product_id: coupon.id,
      coupon_name: coupon.name,
    });
  };

  const handleConfirmUse = () => {
    if (!selectedCoupon) {
      return;
    }

    const coupon = selectedCoupon;

    startCoupon(
      {issueId: coupon.id},
      {
        onSuccess: (data) => {
          setShowConfirm(false);

          if (!data?.result) {
            toast.error(COUPON_MESSAGES.useFailed);
            return;
          }

          tracking(COUPON_EVENTS.startComplete, {
            source: getCouponSource(coupon.issueRoute),
            voucher_product_id: coupon.id,
            days: getCouponDays(coupon),
            coupon_name: coupon.name,
            deadline_d_day: getCouponDeadlineDDay(coupon),
          });

          refetchMe();
          refetchMyCoupons();
          toast.success(COUPON_MESSAGES.useStarted);
          onClose();
          setTimeout(() => navigate(paths.map_root), 1000);
        },
        onError: (error) => {
          // 다른 탭에서 결제·사용된 경우 등 — 최신 상태를 다시 받아오고 모달은 유지한다.
          setShowConfirm(false);
          // 이용 제한은 공통 에러 핸들러가 안내 모달을 띄운다.
          if (!isAccessRestrictionError(error)) {
            toast.error(getGqlErrorMessage(error, COUPON_MESSAGES.useFailed));
          }
          refetchMe();
          refetchMyCoupons();
        },
      },
    );
  };

  const count = coupons?.length ?? 0;
  const isEmpty = !isPending && !isError && !count;

  return (
    <>
      <Modal title="보유 쿠폰" width={520} onClose={onClose}>
        <Body>
          <ColumnFlexBox gap={8}>
            <SectionTitle>쿠폰번호 입력</SectionTitle>
            <Flexbox gap="8px" alignItems="flex-start">
              <TextField
                fullWidth
                value={code}
                placeholder={`쿠폰코드 ${COUPON_CODE_LENGTH}자리`}
                maxLength={COUPON_CODE_LENGTH + 1}
                containerProps={{style: {flexGrow: 1}}}
                onChange={handleChangeCode}
              />
              <RegisterButton
                variant="fill"
                color="primary"
                size="large"
                disabled={isChecking || isRegistering || !code.length}
                onClick={handleRegister}
              >
                {isChecking || isRegistering ? '등록 중' : '등록'}
              </RegisterButton>
            </Flexbox>
            {!!message && (
              <InlineText messageType={message.type}>{message.text}</InlineText>
            )}
          </ColumnFlexBox>

          <ColumnFlexBox gap={12}>
            <Flexbox>
              <SectionTitle>사용 가능한 쿠폰</SectionTitle>
              <FlexFullGrow />
              <Typography variant="medium12" color={palette.gray500}>
                {count}장
              </Typography>
            </Flexbox>
            {hasActivePass && !!count && (
              <NoticeBox>{COUPON_MESSAGES.activePassNotice}</NoticeBox>
            )}
            {isPending ? (
              <PlaceholderBox>
                <Typography variant="medium14" color={palette.gray500}>
                  쿠폰 목록을 불러오는 중입니다.
                </Typography>
              </PlaceholderBox>
            ) : isError ? (
              <PlaceholderBox>
                <Typography variant="medium14" color={palette.gray600}>
                  {COUPON_MESSAGES.listError}
                </Typography>
                <Button
                  variant="outline"
                  color="black"
                  size="regular"
                  onClick={() => refetch()}
                >
                  다시 시도
                </Button>
              </PlaceholderBox>
            ) : isEmpty ? (
              <PlaceholderBox>
                <Typography variant="medium14" color={palette.gray500}>
                  {COUPON_MESSAGES.empty}
                </Typography>
              </PlaceholderBox>
            ) : (
              <ScrollArea>
                {coupons?.map((coupon) => (
                  <CouponListItem
                    key={coupon.id}
                    coupon={coupon}
                    selected={selectedId === coupon.id}
                    disabled={!coupon.usableNow}
                    passEndedAt={hasActivePass ? user?.useEndedAt : undefined}
                    onSelect={handleSelect}
                  />
                ))}
              </ScrollArea>
            )}
          </ColumnFlexBox>
        </Body>
        <Footer>
          <Button
            variant="fill"
            color="primary"
            size="large"
            fullWidth
            disabled={!selectedCoupon?.usableNow || isUsing}
            onClick={() => setShowConfirm(true)}
          >
            사용하기
          </Button>
        </Footer>
      </Modal>
      {showConfirm && !!selectedCoupon && (
        <Dialog
          width={400}
          zIndex={Z_INDEX.modal + 1}
          confirmLabel={isUsing ? '처리 중...' : '사용하기'}
          confirmOptions={{disabled: isUsing}}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleConfirmUse}
        >
          <ConfirmTitle>{COUPON_MESSAGES.confirmTitle}</ConfirmTitle>
          <ConfirmDescription>
            {getCouponConfirmMessage(selectedCoupon)}
          </ConfirmDescription>
          <ConfirmWarning>{COUPON_MESSAGES.confirmWarning}</ConfirmWarning>
        </Dialog>
      )}
    </>
  );
};

const ConfirmTitle = styled.div`
  color: ${palette.gray900};
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  margin-bottom: 8px;
`;

const ConfirmDescription = styled.div`
  color: ${palette.gray600};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.6;
`;

const ConfirmWarning = styled.div`
  margin-top: 16px;
  border-radius: 8px;
  padding: 8px;
  background-color: ${getHexWithOpacity(palette.yellow500, 0.12)};
  color: ${palette.gray800};
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 24px;
`;

const Footer = styled.div`
  padding: 16px 24px 24px;
  flex-shrink: 0;
`;

const SectionTitle = styled.div`
  color: ${palette.gray900};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
`;

const RegisterButton = styled(Button)`
  flex-shrink: 0;
  width: 84px;
  /* TextField variant="large" 높이(42px)에 맞춘다 */
  height: 42px;
  padding: 0 15px;
`;

const InlineText = styled('div').withConfig({
  shouldForwardProp: interceptProps(['messageType']),
})<{messageType: InlineMessage['type']}>`
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  color: ${({messageType}) =>
    messageType === 'error' ? palette.red100 : palette.green500};
`;

const NoticeBox = styled.div`
  border-radius: 8px;
  background-color: ${palette.gray100};
  padding: 12px;
  color: ${palette.gray700};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
`;

const ScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 40vh;
  overflow-y: auto;
`;

const PlaceholderBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  border: dashed 1px ${palette.gray200};
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
`;
